import React, { useEffect, useState } from "react";
import { Textarea, Button } from "../ui";
import { toast } from "react-toastify";
import { refundProcessPayment } from "../../api/paymentsAPI";
import * as paymentsAPI from "../../api/paymentsAPI";
import { cancelCampRegistration } from "../../api/campsAPI";
import { cancelCampRegistration as cancelClinicRegistration } from "../../api/clinicsAPI";
import { cancelRegistration as cancelEventRegistration } from "../../api/eventsAPI";
import { useTheme } from "../../context/ThemeContext";
import {
  getCardBackground,
  getPrimaryText,
  getSecondaryText,
  getInputStyles,
  getLightBorder,
} from "../../utils/themeUtils";
import LoadingSpinner from "../common/LoadingSpinner";

interface RefundModalProps {
  isOpen: boolean;
  onClose: () => void;
  paymentId: string;
  invoiceId?: string;
  onRefundSuccess?: () => void;
  // Cancelled-registration support. When the payment being refunded is a
  // registration payment, its invoice carries paidForInvoiceId → the charge
  // invoice. Passing the linked charge here surfaces an opt-in "also void the
  // related charge" checkbox so a full refund nets the member to $0 instead of
  // leaving a phantom balance. Only shown on a FULL refund.
  paymentInvoiceId?: string;
  linkedChargeInvoiceId?: string;
  linkedChargeAmount?: number;
  linkedChargeDescription?: string;
  // The camp/clinic/event registration this charge belongs to. When the staffer
  // voids the charge on refund, we offer to also cancel this registration so the
  // roster and billing stay in sync (reverse-sync of the void).
  linkedRegistration?: {
    entityType: "camp" | "clinic" | "event";
    entityId: string;
    registrationId: string;
    entityName?: string;
  } | null;
}

interface RefundEligibility {
  isEligible: boolean;
  originalAmount: number;
  refundedAmount: number;
  refundableAmount: number;
  paymentType: string;
  paymentStatus?: string;
  paymentDate?: string;
  achWarning?: string;
  achClearingRequired?: boolean;
  daysRemaining?: number;
  transactionId?: string;
  message?: string;
  error?: string;
}

const REFUND_REASONS = [
  { value: "requested_by_customer", label: "Requested by Customer" },
  { value: "duplicate", label: "Duplicate Payment" },
  { value: "fraudulent", label: "Fraudulent Payment" },
];

const RefundModal: React.FC<RefundModalProps> = ({
  isOpen,
  onClose,
  paymentId,
  invoiceId,
  onRefundSuccess,
  paymentInvoiceId,
  linkedChargeInvoiceId,
  linkedChargeAmount,
  linkedChargeDescription,
  linkedRegistration,
}) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [eligibilityLoading, setEligibilityLoading] = useState(true);
  const [eligibility, setEligibility] = useState<RefundEligibility | null>(null);
  const [isFullRefund, setIsFullRefund] = useState(true);
  const [partialAmount, setPartialAmount] = useState("");
  const [reason, setReason] = useState("requested_by_customer");
  const [note, setNote] = useState("");
  // Opt-in: also void the linked registration charge (cancelled registration).
  const [voidLinkedCharge, setVoidLinkedCharge] = useState(false);
  const hasLinkedCharge = Boolean(linkedChargeInvoiceId);
  // Reverse-sync step: after a void+refund, offer to cancel the registration.
  const [regCancelStep, setRegCancelStep] = useState(false);
  const [regCancelLoading, setRegCancelLoading] = useState(false);

  const token = localStorage.getItem("authToken");

  // Fetch eligibility when modal opens
  useEffect(() => {
    if (isOpen && paymentId) {
      fetchEligibility();
    }
  }, [isOpen, paymentId]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsFullRefund(true);
      setPartialAmount("");
      setReason("requested_by_customer");
      setNote("");
      setEligibility(null);
      setVoidLinkedCharge(false);
      setRegCancelStep(false);
      setRegCancelLoading(false);
    }
  }, [isOpen]);

  // Voiding the related charge only makes sense for a full reversal — drop the
  // flag if the staffer switches to a partial refund.
  useEffect(() => {
    if (!isFullRefund && voidLinkedCharge) setVoidLinkedCharge(false);
  }, [isFullRefund, voidLinkedCharge]);

  const fetchEligibility = async () => {
    setEligibilityLoading(true);
    try {
      const result = await (paymentsAPI as any).getRefundEligibility(token, paymentId);
      setEligibility(result);
      if (result?.refundableAmount) {
        setPartialAmount(result.refundableAmount.toFixed(2));
      }
    } catch (error) {
      toast.error("Failed to check refund eligibility");
    } finally {
      setEligibilityLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!eligibility?.isEligible) {
      toast.error("This payment is not eligible for refund");
      return;
    }

    const refundAmount = isFullRefund
      ? eligibility.refundableAmount
      : parseFloat(partialAmount);

    if (!refundAmount || refundAmount <= 0) {
      toast.error("Please enter a valid refund amount");
      return;
    }

    if (refundAmount > eligibility.refundableAmount) {
      toast.error(
        `Refund amount cannot exceed $${eligibility.refundableAmount.toFixed(2)}`
      );
      return;
    }

    setLoading(true);

    try {
      const result = await refundProcessPayment(
        {
          paymentId,
          invoiceId,
          refundAmount,
          reason,
          note: note || "Refund issued via admin panel",
          // Only a full refund of a payment with a linked charge can void it.
          voidLinkedCharge: voidLinkedCharge && hasLinkedCharge && isFullRefund,
          linkedChargeInvoiceId: linkedChargeInvoiceId || null,
          paymentInvoiceId: paymentInvoiceId || invoiceId || null,
        },
        token
      );

      if (result?.success) {
        // Bank payments are stored as "bank_account"; Stripe calls them "us_bank_account".
        const isAch = eligibility.paymentType === "us_bank_account" || eligibility.paymentType === "bank_account";
        toast.success(
          isAch
            ? "Refund initiated! ACH refunds take 3-5 business days to process."
            : "Refund processed successfully!"
        );
        onRefundSuccess?.();
        // If we voided a registration charge and the charge belongs to a
        // camp/clinic/event registration, offer to cancel that registration too
        // (the void only fixed billing; the roster still lists the member).
        const didVoid = voidLinkedCharge && hasLinkedCharge && isFullRefund;
        if (didVoid && linkedRegistration) {
          setRegCancelStep(true);
        } else {
          onClose();
        }
      } else {
        toast.error(result?.error || "Failed to process refund");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to process refund");
    } finally {
      setLoading(false);
    }
  };

  // Reverse-sync: cancel the registration the voided charge belonged to.
  const cancelLinkedRegistration = async () => {
    if (!linkedRegistration) {
      onClose();
      return;
    }
    setRegCancelLoading(true);
    try {
      const { entityType, entityId, registrationId } = linkedRegistration;
      if (entityType === "camp") {
        await cancelCampRegistration(token, entityId, registrationId);
      } else if (entityType === "clinic") {
        await cancelClinicRegistration(token, entityId, registrationId);
      } else if (entityType === "event") {
        await cancelEventRegistration(token, entityId, registrationId);
      }
      toast.success("Registration canceled.");
      onRefundSuccess?.();
      onClose();
    } catch (error: any) {
      toast.error(error?.message || "Failed to cancel registration");
    } finally {
      setRegCancelLoading(false);
    }
  };

  if (!isOpen) return null;

  const getPaymentTypeDisplay = (type: string) => {
    const types: Record<string, string> = {
      card: "Credit/Debit Card",
      us_bank_account: "Bank Account (ACH)",
      bank_account: "Bank Account (ACH)",
      cash: "Cash",
      check: "Check",
    };
    return types[type] || type;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className={`${getCardBackground(isDarkMode)} rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden`}
      >
        {/* Header */}
        <div className={`px-6 py-4 border-b ${getLightBorder(isDarkMode)}`}>
          <div className="flex justify-between items-center">
            <h2 className={`text-xl font-semibold ${getPrimaryText(isDarkMode)}`}>
              Issue Refund
            </h2>
            <Button
              type="button"
              variant="neutral"
              size="sm"
              iconOnly
              aria-label="Close"
              onClick={onClose}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          {regCancelStep && linkedRegistration ? (
            <div>
              <div
                className={`mb-4 p-4 rounded-lg ${
                  isDarkMode ? "bg-gray-700" : "bg-amber-50 border border-amber-300"
                }`}
              >
                <p className={`font-medium ${getPrimaryText(isDarkMode)}`}>
                  Refund processed and the charge was voided.
                </p>
                <p className={`mt-1 text-sm ${getSecondaryText(isDarkMode)}`}>
                  This payment was for a {linkedRegistration.entityType}
                  {linkedRegistration.entityName ? ` — "${linkedRegistration.entityName}"` : ""}.
                  The member is still on the roster. Cancel the registration too?
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={regCancelLoading}
                  className={`flex-1 py-2 px-4 rounded-lg border ${getLightBorder(isDarkMode)} ${getPrimaryText(isDarkMode)} hover:opacity-80 disabled:opacity-50`}
                >
                  No, keep registration
                </button>
                <button
                  type="button"
                  onClick={cancelLinkedRegistration}
                  disabled={regCancelLoading}
                  className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center"
                >
                  {regCancelLoading ? (
                    <>
                      <LoadingSpinner size="small" color="white" />
                      <span className="ml-2">Cancelling...</span>
                    </>
                  ) : (
                    `Cancel ${linkedRegistration.entityType} registration`
                  )}
                </button>
              </div>
            </div>
          ) : eligibilityLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="medium" color="blue" />
            </div>
          ) : eligibility?.error ? (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {eligibility.error}
            </div>
          ) : !eligibility?.isEligible && eligibility?.achClearingRequired ? (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded">
              <p className="font-semibold">ACH Clearing Required</p>
              <p className="mt-1">{eligibility.achWarning}</p>
              <p className="mt-2 text-sm">
                Please wait {eligibility.daysRemaining} more day(s) before
                processing this refund.
              </p>
            </div>
          ) : !eligibility?.isEligible ? (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded">
              {eligibility?.message || "This payment is not eligible for refund"}
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Payment Info */}
              <div
                className={`mb-4 p-4 rounded-lg ${isDarkMode ? "bg-gray-700" : "bg-gray-100"}`}
              >
                <div className="flex justify-between mb-2">
                  <span className={getSecondaryText(isDarkMode)}>
                    Original Amount:
                  </span>
                  <span className={`font-semibold ${getPrimaryText(isDarkMode)}`}>
                    ${eligibility.originalAmount?.toFixed(2)}
                  </span>
                </div>
                {eligibility.refundedAmount > 0 && (
                  <div className="flex justify-between mb-2">
                    <span className={getSecondaryText(isDarkMode)}>
                      Already Refunded:
                    </span>
                    <span className="font-semibold text-orange-600">
                      ${eligibility.refundedAmount?.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between mb-2">
                  <span className={getSecondaryText(isDarkMode)}>
                    Available for Refund:
                  </span>
                  <span className="font-semibold text-green-600">
                    ${eligibility.refundableAmount?.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={getSecondaryText(isDarkMode)}>
                    Payment Method:
                  </span>
                  <span className={getPrimaryText(isDarkMode)}>
                    {getPaymentTypeDisplay(eligibility.paymentType)}
                  </span>
                </div>
              </div>

              {/* ACH Warning */}
              {(eligibility.paymentType === "us_bank_account" || eligibility.paymentType === "bank_account") && (
                <div className="mb-4 p-3 bg-blue-100 border border-blue-400 text-blue-800 rounded">
                  <p className="text-sm">
                    <strong>Note:</strong> ACH refunds take 3-5 business days to
                    process.
                  </p>
                </div>
              )}

              {/* Refund Type Toggle */}
              <div className="mb-4">
                <label className={`block mb-2 font-medium ${getPrimaryText(isDarkMode)}`}>
                  Refund Type
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={isFullRefund}
                      onChange={() => setIsFullRefund(true)}
                      className="mr-2"
                    />
                    <span className={getPrimaryText(isDarkMode)}>
                      Full Refund (${eligibility.refundableAmount?.toFixed(2)})
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={!isFullRefund}
                      onChange={() => setIsFullRefund(false)}
                      className="mr-2"
                    />
                    <span className={getPrimaryText(isDarkMode)}>
                      Partial Refund
                    </span>
                  </label>
                </div>
              </div>

              {/* Partial Amount Input */}
              {!isFullRefund && (
                <div className="mb-4">
                  <label
                    className={`block mb-2 font-medium ${getPrimaryText(isDarkMode)}`}
                  >
                    Refund Amount
                  </label>
                  <div className="relative">
                    <span
                      className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${getSecondaryText(isDarkMode)}`}
                    >
                      $
                    </span>
                    <input
                      aria-label="Refund amount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={eligibility.refundableAmount}
                      value={partialAmount}
                      onChange={(e) => setPartialAmount(e.target.value)}
                      className={`w-full pl-7 pr-4 py-2 rounded-lg ${getInputStyles(isDarkMode)}`}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              )}

              {/* Void linked charge (cancelled registration) — only on a full
                  refund of a payment that has a linked registration charge. */}
              {hasLinkedCharge && isFullRefund && (
                <div
                  className={`mb-4 p-3 rounded-lg border ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-amber-50 border-amber-300"
                  }`}
                >
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={voidLinkedCharge}
                      onChange={(e) => setVoidLinkedCharge(e.target.checked)}
                      className="mt-1 mr-2 accent-[var(--cc-action)]"
                    />
                    <span className={`text-sm ${getPrimaryText(isDarkMode)}`}>
                      <span className="font-medium">
                        Also void the related charge
                        {typeof linkedChargeAmount === "number"
                          ? ` ($${linkedChargeAmount.toFixed(2)})`
                          : ""}
                      </span>
                      <span className={`block mt-0.5 ${getSecondaryText(isDarkMode)}`}>
                        Use this when the registration is canceled — it zeroes
                        {linkedChargeDescription
                          ? ` "${linkedChargeDescription}"`
                          : " the linked registration charge"}{" "}
                        and this payment so the member's balance nets to $0.
                        Leave unchecked for a goodwill refund where the charge
                        still stands.
                      </span>
                    </span>
                  </label>
                </div>
              )}

              {/* Reason Dropdown */}
              <div className="mb-4">
                <label
                  className={`block mb-2 font-medium ${getPrimaryText(isDarkMode)}`}
                >
                  Reason
                </label>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg ${getInputStyles(isDarkMode)}`}
                >
                  {REFUND_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Note Field */}
              <div className="mb-4">
                <Textarea
                  label="Note (Optional)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  placeholder="Additional notes about this refund..."
                />
              </div>

              {/* Submit Button */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className={`flex-1 py-2 px-4 rounded-lg border ${getLightBorder(isDarkMode)} ${getPrimaryText(isDarkMode)} hover:opacity-80`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-2 px-4 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="small" color="white" />
                      <span className="ml-2">Processing...</span>
                    </>
                  ) : (
                    `Refund $${(isFullRefund ? eligibility.refundableAmount : parseFloat(partialAmount) || 0).toFixed(2)}`
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default RefundModal;
